/**
 * Harshness detection for chord cards.
 *
 * Non-anxiogenic warning system: "heads up, this one can bite, use your ears".
 * Returns true for chords that are objectively dissonant in a way a beginner
 * will hear as "wrong" on first listen.
 *
 * Intentionally conservative: 0 flags in Essentials, a few in Colors, majority in Wild.
 *
 * See PLAYGROUND v1 Product Direction.
 */

/**
 * Normalize an array of semitone intervals to a pitch-class Set (0..11).
 */
const toPcSet = (intervals) => {
  const set = new Set();
  intervals.forEach(i => set.add(((i % 12) + 12) % 12));
  return set;
};

/**
 * Count the number of pairs at a given circular distance in a pitch-class set.
 * Circular distance = min(d, 12 - d).
 */
const countPairsAtDistance = (pcSet, distance) => {
  const arr = [...pcSet].sort((a, b) => a - b);
  let count = 0;
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      const d = arr[j] - arr[i];
      const dist = Math.min(d, 12 - d);
      if (dist === distance) count++;
    }
  }
  return count;
};

const containsAll = (pcSet, ...vals) => vals.every(v => pcSet.has(v));

/**
 * Determine whether the chord formed by `intervals` (semitones from root) is harsh.
 * `intervals` can be root-relative semitones (e.g. [0, 4, 7, 11]) or any interval list;
 * they are reduced to pitch classes internally.
 *
 * @param {number[]} intervals
 * @returns {boolean}
 */
export const isHarshChord = (intervals) => {
  if (!Array.isArray(intervals) || intervals.length < 3) return false;

  const pc = toPcSet(intervals);
  const minorSeconds = countPairsAtDistance(pc, 1);
  const tritones = countPairsAtDistance(pc, 6);

  // Rule 1: a minor 2nd co-existing with a tritone. Classic cluster + b5 tension.
  if (minorSeconds >= 1 && tritones >= 1) return true;

  // Rule 2: 2+ minor 2nds in one chord (dense accidental cluster).
  if (minorSeconds >= 2) return true;

  // Rule 3: 2+ tritones in one chord (symmetric diminished territory).
  if (tritones >= 2) return true;

  // Rule 4: augMaj7 [0, 4, 8, 11] — flagged by spec.
  if (containsAll(pc, 0, 4, 8, 11)) return true;

  // Rule 5: plain augmented triad (III in Melodic Minor, any whole-tone root).
  if (pc.size === 3 && containsAll(pc, 0, 4, 8)) return true;

  return false;
};

/**
 * Convenience: compute harshness from an array of note names and a reference root.
 * Returns false if any note can't be resolved.
 *
 * @param {string[]} noteNames
 * @param {(note: string) => number} resolveIndex - e.g. resolveAllNotesIndex
 * @returns {boolean}
 */
export const isHarshChordFromNotes = (noteNames, resolveIndex) => {
  if (!Array.isArray(noteNames) || noteNames.length < 3) return false;
  const rootIdx = resolveIndex(noteNames[0]);
  if (rootIdx === -1) return false;
  const intervals = noteNames.map(n => {
    const idx = resolveIndex(n);
    if (idx === -1) return null;
    return ((idx - rootIdx) + 12) % 12;
  });
  if (intervals.some(v => v === null)) return false;
  return isHarshChord(intervals);
};
